import { computed, effect, Injectable, signal } from '@angular/core'

import {
  DEFAULT_PAGE,
  PortabilityRequestsBaseService,
} from './portability-requests-base.service'
import { ApiService } from '../api/api.service'
import {
  IRequestLineBase,
  IRequestLineQualification,
  IRequestLineQualificationPaginated,
  IRequestMatchListState,
  ISearchParams,
  PortabilityRequestList,
} from '../../models/portability-request.model'
import { HttpErrorResponse } from '@angular/common/http'
import { AuthService } from '../auth.service'
import { NotificationService } from '../notification.service'

const requestMatchesStateTemplate = {
  requestMatchList: [],
  loading: false,
  error: null,
}

@Injectable({
  providedIn: 'root',
})
export class AdminRequestsQualificationService
  extends PortabilityRequestsBaseService<IRequestLineQualification>
  implements PortabilityRequestList
{
  readonly API_ENDPOINT = '/portability-request?qualificationOnly=true'
  readonly SUBMIT_QUALIFICATION_API_ENDPOINT =
    '/portability-request/{id}/qualification?'
  readonly REJECT_QUALIFICATION_API_ENDPOINT =
    '/portability-request/{id}/qualification/reject'
  readonly QUALIFICATION_COUNT_API_ENDPOINT =
    '/portability-request/qualification/count'

  protected requestMatchesState = signal<IRequestMatchListState>(
    requestMatchesStateTemplate,
  )

  qualificationCount = signal<number | null>(null)

  public requestMatchList = computed(
    () => this.requestMatchesState().requestMatchList,
  )
  public requestMatchesLoading = computed(
    () => this.requestMatchesState().loading,
  )
  public requestMatchesError = computed(() => this.requestMatchesState().error)

  public requestMatchListCount = computed(() => this.requestMatchList().length)

  constructor(
    protected override apiService: ApiService,
    protected override notificationService: NotificationService,
    private authService: AuthService,
  ) {
    super(apiService, notificationService)
  }

  get isLoggedIn() {
    return this.authService.isLoggedIn()
  }

  effect = effect(() => {
    const currentRole = this.authService.currentRole()

    if (this.isLoggedIn && currentRole === 'admin') {
      this.fetchPortabilityRequestQualificationCount()
    }
  })

  protected setRequestMatchesLoading(loading: boolean): void {
    this.requestMatchesState.update((requestMatchesState) => ({
      ...requestMatchesState,
      loading,
    }))
  }

  public clearRequestMatchesState(): void {
    this.requestMatchesState.set(requestMatchesStateTemplate)
  }

  protected clearRequestMatchesError(): void {
    this.requestMatchesState.update((requestMatchesState) => ({
      ...requestMatchesState,
      error: null,
    }))
  }

  protected handleRequestMatchesError(error: HttpErrorResponse): void {
    this.requestMatchesState.update((requestMatchesState) => ({
      ...requestMatchesState,
      error: error.message,
      loading: false,
    }))
  }

  protected updateRequestMatchesState(
    partialState: Partial<IRequestMatchListState>,
  ): void {
    this.requestMatchesState.update((requestMatchesState) => ({
      ...requestMatchesState,
      ...partialState,
    }))
  }

  public fetchPortabilityRequestList() {
    this.setLoading(true)
    this.clearError()

    return this.apiService
      .get<IRequestLineQualificationPaginated>(this.API_ENDPOINT, DEFAULT_PAGE)
      .subscribe({
        next: (requestListPaginated) => {
          this.state.update((state) => ({
            ...state,
            requestList: requestListPaginated.content,
            size: requestListPaginated.page.size,
            totalElements: requestListPaginated.page.totalElements,
            loading: false,
          }))
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error)
        },
      })
  }

  public searchPortabilityRequestList(
    criteria: Partial<ISearchParams>,
    firstElementOfTable: number,
  ): void {
    this.setLoading(true)
    this.clearError()

    this.apiService
      .get<IRequestLineQualificationPaginated>(this.API_ENDPOINT, {
        ...criteria,
      })
      .subscribe({
        next: (requestListPaginated) => {
          this.updateState({
            requestList: requestListPaginated.content,
            loading: false,
            size: requestListPaginated.page.size,
            totalElements: requestListPaginated.page.totalElements,
            first:
              requestListPaginated.page.number === 0 ? 0 : firstElementOfTable,
          })
          this.setLoading(false)
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error)
        },
      })
  }

  public fetchPortabilityRequestMatchList(
    qualifications: IRequestLineBase[],
  ): void {
    this.setRequestMatchesLoading(true)
    this.clearRequestMatchesError()

    this.requestMatchesState.update((state) => ({
      ...state,
      requestMatchList: qualifications,
      loading: false,
    }))
  }

  submitPortabilityRequestQualification(
    portabilityRequestId: string,
    qualificationIds: string[],
  ) {
    const formData = new FormData()

    // TODO: mettre les identifiers dans le body (formData) quand le backend sera à jour
    const identifiers = qualificationIds
      .map((id) => 'identifiers=' + id)
      .join('&')

    return this.apiService.post(
      this.SUBMIT_QUALIFICATION_API_ENDPOINT.replace(
        '{id}',
        portabilityRequestId,
      ) + identifiers,
      formData,
    )
  }

  rejectPortabilityRequestQualification(portabilityRequestId: string) {
    const formData = new FormData()
    return this.apiService.post(
      this.REJECT_QUALIFICATION_API_ENDPOINT.replace(
        '{id}',
        portabilityRequestId,
      ),
      formData,
    )
  }

  public fetchPortabilityRequestQualificationCount(): void {
    this.apiService
      .get<{ counter: number }>(this.QUALIFICATION_COUNT_API_ENDPOINT)
      .subscribe({
        next: (result) => {
          this.qualificationCount.set(
            'counter' in result && result['counter'] !== 0
              ? result['counter']
              : null,
          )
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error)
        },
      })
  }
}
